// MicKernel — the one shared module for microphone plumbing.
// typed errors, aliveness probe, health watcher, device listing. No options
// beyond what callers use today; telemetry stays in the callers.

const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const classifyError = (error) => {
  switch (error && error.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "denied";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "no_device";
    case "NotReadableError":
    case "TrackStartError":
      return "busy";
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return "stale_id";
    default:
      return "unknown";
  }
};

// Acquire an audio stream. `deviceId` is a soft preference (ideal): a stale id
// falls back to the default device instead of throwing OverconstrainedError.
export const acquireMic = async ({ deviceId } = {}) => {
  const constraints = {
    audio: {
      ...AUDIO_CONSTRAINTS,
      ...(deviceId ? { deviceId: { ideal: deviceId } } : {}),
    },
  };
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    const track = stream.getAudioTracks()[0];
    let settings = null;
    try {
      settings = track.getSettings();
    } catch (_) {}
    return {
      ok: true,
      stream,
      track,
      label: (track && track.label) || null,
      settings,
    };
  } catch (error) {
    return { ok: false, cause: classifyError(error), error };
  }
};

// Is the pipeline actually delivering samples? Zeros ≠ quiet: a live mic in a
// silent room still dithers above exact 0, so any nonzero byte proves life.
// alive === null means the PROBE infrastructure failed (not the mic) — callers
// using this as a gate must fail OPEN on null, or a suspended AudioContext
// would mint false SIGNAL_DEAD walls.
export const probeAliveness = async (stream, { timeoutMs = 1500 } = {}) => {
  let ctx;
  try {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
  } catch (_) {
    return { alive: null, first_nonzero_ms: null, error: "no_audiocontext" };
  }
  try {
    if (ctx.state === "suspended") await ctx.resume();
  } catch (_) {}
  if (ctx.state !== "running") {
    ctx.close().catch(() => {});
    return {
      alive: null,
      first_nonzero_ms: null,
      error: "audio_context_suspended",
    };
  }
  return new Promise((resolve) => {
    const finish = (result) => {
      clearInterval(timer);
      try {
        source.disconnect();
      } catch (_) {}
      ctx.close().catch(() => {});
      resolve(result);
    };
    let source;
    let analyser;
    try {
      source = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0;
      source.connect(analyser);
    } catch (error) {
      ctx.close().catch(() => {});
      resolve({ alive: null, first_nonzero_ms: null, error: String(error) });
      return;
    }
    // Time-domain RMS, not getByteFrequencyData: on a dead/disconnected
    // track an FFT over stale buffered frames can read spurious nonzero
    // bins, false-passing a genuinely dead device. Raw samples are exact —
    // true digital silence is exactly 0.0, same convention as runSpeechCheck.
    const buf = new Float32Array(analyser.fftSize);
    const started = Date.now();
    const timer = setInterval(() => {
      analyser.getFloatTimeDomainData(buf);
      for (let i = 0; i < buf.length; i++) {
        if (buf[i] !== 0) {
          finish({ alive: true, first_nonzero_ms: Date.now() - started });
          return;
        }
      }
      if (Date.now() - started >= timeoutMs) {
        finish({ alive: false, first_nonzero_ms: null });
      }
    }, 50);
  });
};

// Watch a live track's health: death events, device-list changes, and a 1 Hz
// silence sampler (the only detector for event-less OS-mute). Returns stop().
// onZeroWindow (zeroWindowSec) is telemetry; onZeroTrip (zeroTripSec) is
// destructive and waits longer — noise gates emit exact zeros during pauses.
export const watchHealth = (
  track,
  {
    stream,
    onEnded,
    onMute,
    onUnmute,
    onDeviceChange,
    onZeroWindow,
    onZeroTrip,
    zeroWindowSec = 3,
    zeroTripSec = 10,
  } = {}
) => {
  const handlers = [];
  const on = (target, event, fn) => {
    target.addEventListener(event, fn);
    handlers.push(() => target.removeEventListener(event, fn));
  };

  if (onEnded) on(track, "ended", onEnded);
  if (onMute) on(track, "mute", onMute);
  if (onUnmute) on(track, "unmute", onUnmute);
  if (onDeviceChange && navigator.mediaDevices)
    on(navigator.mediaDevices, "devicechange", onDeviceChange);

  // Silence sampler on a dedicated context/analyser — never touches the
  // caller's audio graph.
  let ctx = null;
  let sampler = null;
  if (stream && (onZeroWindow || onZeroTrip)) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0;
      source.connect(analyser);
      // Time-domain RMS, not getByteFrequencyData: on a genuinely dead or
      // disconnected track (e.g. unplugged mid-recording, no `ended` event
      // fired) an FFT over stale buffered frames can read spurious nonzero
      // bins, silently missing the death. Raw samples are exact — true
      // digital silence is exactly 0.0, same check as probeAliveness.
      const buf = new Float32Array(analyser.fftSize);
      let zeroSeconds = 0;
      let windowReported = false;
      let tripReported = false;
      sampler = setInterval(() => {
        // non-running context reads all zeros — probe silence, not mic silence
        if (ctx.state !== "running") return;
        analyser.getFloatTimeDomainData(buf);
        let nonzero = false;
        for (let i = 0; i < buf.length; i++) {
          if (buf[i] !== 0) {
            nonzero = true;
            break;
          }
        }
        if (nonzero) {
          zeroSeconds = 0;
          windowReported = false;
          tripReported = false;
        } else {
          zeroSeconds++;
          if (onZeroWindow && zeroSeconds >= zeroWindowSec && !windowReported) {
            windowReported = true;
            onZeroWindow({ zero_seconds: zeroSeconds });
          }
          if (onZeroTrip && zeroSeconds >= zeroTripSec && !tripReported) {
            tripReported = true;
            onZeroTrip({ zero_seconds: zeroSeconds });
          }
        }
      }, 1000);
    } catch (_) {
      // sampler is best-effort; event watchers still run
    }
  }

  return {
    stop() {
      handlers.forEach((off) => off());
      if (sampler) clearInterval(sampler);
      if (ctx) ctx.close().catch(() => {});
    },
  };
};

export const enumerateMics = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices)
    return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === "audioinput")
    .map((d) => ({ deviceId: d.deviceId, label: d.label, groupId: d.groupId }));
};

export const SPEECH_CHECK_DEFAULTS = {
  tickMs: 50, // setInterval clock — never rAF (background tabs pause rAF)
  fftSize: 2048, // 2048 float samples ≈ 42.7ms @48kHz, matches the 50ms tick
  calibrationMs: 600,
  maxListenMs: 10000,
  voicedTargetMs: 300, // early pass on 300ms of cumulative voiced time
  floorMultiplier: 3,
  thresholdMin: 0.01, // absolute RMS floor for the threshold
  thresholdMax: 0.08, // cap: keeps speak-during-calibration voiced
  maxTickCreditMs: 100, // per-tick voiced credit clamp (throttled-tab guard)
};

// RMS (0..1) → meter percent: −50 dBFS → 0, −10 dBFS → 100.
// Room noise ≈ 0%, soft speech ≈ 40%, normal ≈ 75%, loud ≈ 100.
export const rmsToPercent = (rms) => {
  const db = 20 * Math.log10(Math.max(rms, 1e-8));
  return Math.max(0, Math.min(100, Math.round(((db + 50) / 40) * 100)));
};

// Speech detection: relative RMS threshold over time-domain samples.
// Returns { promise, cancel(reason) }. cancel RESOLVES the promise with
// verdict "cancelled" (never rejects). Owns and closes its own AudioContext
// on every exit path; never stops the caller's stream.
export const runSpeechCheck = (stream, { onLevel } = {}) => {
  const cfg = SPEECH_CHECK_DEFAULTS;
  let finished = false;
  let timer = null;
  let ctx = null;
  let source = null;
  let resolvePromise;
  const promise = new Promise((resolve) => {
    resolvePromise = resolve;
  });

  const startedAt = Date.now();
  const state = {
    rms_peak: 0,
    voiced_rms_peak: 0,
    noise_floor: null,
    threshold: null,
    voiced_ms: 0,
    voiced_frames: 0,
    frames: 0,
    first_voiced_ms: null,
    first_nonzero_ms: null,
    floor_zero: false,
  };
  let contextMs = 0;
  let contextStateAtCreate = null;

  const finish = (extra) => {
    if (finished) return;
    finished = true;
    if (timer) clearInterval(timer);
    try {
      if (source) source.disconnect();
    } catch (_) {}
    if (ctx) ctx.close().catch(() => {});
    resolvePromise({
      ...state,
      elapsed_ms: Date.now() - startedAt,
      tick_interval_avg_ms:
        state.frames > 0
          ? Math.round((Date.now() - startedAt) / state.frames)
          : null,
      context_ms: contextMs,
      context_state_at_create: contextStateAtCreate,
      ...extra,
    });
  };

  (async () => {
    const t0 = Date.now();
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (_) {
      finish({ verdict: "error", error: "no_audiocontext" });
      return;
    }
    contextStateAtCreate = ctx.state;
    try {
      if (ctx.state === "suspended") await ctx.resume();
    } catch (_) {}
    contextMs = Date.now() - t0;
    if (finished) return; // cancelled during resume
    if (ctx.state !== "running") {
      finish({ verdict: "error", error: "audio_context_suspended" });
      return;
    }
    let analyser;
    try {
      source = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = cfg.fftSize;
      analyser.smoothingTimeConstant = 0;
      source.connect(analyser);
    } catch (error) {
      finish({ verdict: "error", error: String(error) });
      return;
    }
    const buf = new Float32Array(analyser.fftSize);
    const calibration = [];
    let lastTickAt = Date.now();
    timer = setInterval(() => {
      if (finished) return;
      const now = Date.now();
      const sinceStart = now - startedAt;
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      let nonzero = false;
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i];
        sum += v * v;
        if (v !== 0) nonzero = true;
      }
      const rms = Math.sqrt(sum / buf.length);
      state.frames++;
      if (rms > state.rms_peak) state.rms_peak = rms;
      if (nonzero && state.first_nonzero_ms === null)
        state.first_nonzero_ms = sinceStart;
      if (onLevel) {
        try {
          onLevel(rmsToPercent(rms), rms);
        } catch (_) {}
      }

      if (state.threshold === null) {
        calibration.push(rms);
        if (sinceStart >= cfg.calibrationMs) {
          // 20th percentile: robust to a candidate speaking mid-calibration
          const sorted = [...calibration].sort((a, b) => a - b);
          const floor =
            sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.2))] ||
            0;
          state.noise_floor = floor;
          state.floor_zero = floor === 0;
          state.threshold = Math.min(
            cfg.thresholdMax,
            Math.max(cfg.thresholdMin, floor * cfg.floorMultiplier)
          );
        }
      } else if (rms > state.threshold) {
        state.voiced_frames++;
        if (rms > state.voiced_rms_peak) state.voiced_rms_peak = rms;
        if (state.first_voiced_ms === null) state.first_voiced_ms = sinceStart;
        state.voiced_ms += Math.min(now - lastTickAt, cfg.maxTickCreditMs);
        if (state.voiced_ms >= cfg.voicedTargetMs) {
          lastTickAt = now;
          finish({ verdict: "passed" });
          return;
        }
      }
      lastTickAt = now;
      if (sinceStart >= cfg.maxListenMs) finish({ verdict: "no_speech" });
    }, cfg.tickMs);
  })();

  return {
    promise,
    cancel(reason) {
      finish({ verdict: "cancelled", cancel_reason: reason || "cancelled" });
    },
  };
};
